import { useEffect } from 'react';
import { useMaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { Box, IconButton, MenuItem, Tooltip } from '@mui/material';
import IconTrash from 'src/presentations/components/atom/icons/Trash';
import IconNewEdit from 'src/presentations/components/atom/icons/newedit.svg';
import { styled } from '@mui/material/styles';
import { CustomTable } from 'src/presentations/components/molecules/table/Table';
import { useFormContext } from 'react-hook-form';
import { createTableConfig } from 'src/presentations/utils/tableConfigUtils';
import { ShipmentChargeDto } from 'src/generated/services/TFinancialApi';
import { beautifyEnum, formatNumber } from 'src/utils/utility';
import { NoContractForm } from './types';
import { getCommonPrefixColumns, getCommonSuffixColumns } from './commonChargeColumns';

// Common currencies used in freight forwarding
const CURRENCIES = ['USD', 'EUR', 'GBP', 'JPY', 'CNY', 'SGD', 'AUD', 'CAD', 'CHF', 'HKD', 'INR', 'MYR', 'THB', 'KRW'];

const firstCellStyles = {
  boxShadow: '4px 0px 12px 0px rgba(89, 114, 130, 0.08)',
  borderLeft: '1px solid transparent',
  width: '120px',
  padding: '0 10px 0 10px !important',

  '& .MuiBox-root': {
    gap: '0!important',
    width: '90px',
    justifyContent: 'center',

    '& svg': {
      width: '20px!important',
    },
  },
};

/**
 * Styled component for the main table container.
 */
export const TableMain = styled(Box)({
  '.MuiTableCell-head:first-of-type': {
    width: '40px',
  },
});

/**
 * Hook to define the columns for the shipment charges table.
 *
 * @returns {MRT_ColumnDef<MergedShipmentDeclarationChargeDto>[]} Array of column definitions.
 */
const useChargesShipmentColumns = (): MRT_ColumnDef<ShipmentChargeDto>[] => {
  const commonPrefix = getCommonPrefixColumns<ShipmentChargeDto>();
  const commonSuffix = getCommonSuffixColumns<ShipmentChargeDto>();

  // Shipment-specific columns
  const shipmentSpecific: MRT_ColumnDef<ShipmentChargeDto>[] = [
    {
      accessorKey: 'unitPrice',
      header: 'Unit Price',
      Cell: ({ row }) => {
        const value = row?.original?.unitPrice;
        if (value !== null && value !== undefined) {
          return <span className='text-14 xl:text-16'>{formatNumber(parseFloat(value.toString()))}</span>;
        }
        return <span className='text-14 xl:text-16'>-</span>;
      },
      muiEditTextFieldProps: {
        type: 'number',
        required: true,
      },
    },
    {
      accessorKey: 'currency',
      header: 'Currency',
      Cell: ({ row }) => <span className='text-14 xl:text-16'>{row?.original?.currency || '-'}</span>,
      muiEditTextFieldProps: {
        select: true,
        required: true,
        children: CURRENCIES.map((currency) => (
          <MenuItem key={currency} value={currency}>
            {currency}
          </MenuItem>
        )),
      },
    },
  ];

  return [...commonPrefix, ...shipmentSpecific, ...commonSuffix];
};

/**
 * Props for the TableChargesShipment component.
 *
 * @typedef {Object} ITableChargesShipmentProps
 * @property {MergedShipmentDeclarationChargeDto[]} data - The data to display in the table.
 * @property {boolean} [enableEditing=false] - Flag to enable or disable editing of the table rows.
 * @property {number | null} [editingRowIndex] - Index of the row to edit.
 * @property {(index: number | null) => void} [onEditingRowChange] - Callback when editing row changes.
 */
export interface ITableChargesShipmentProps {
  data: ShipmentChargeDto[];
  enableEditing?: boolean;
  editingRowIndex?: number | null;
  onEditingRowChange?: (index: number | null) => void;
  onEditingChange?: (isEditing: boolean) => void;
}

export const TableChargesShipment = ({ data, enableEditing = false, editingRowIndex, onEditingRowChange, onEditingChange }: ITableChargesShipmentProps) => {
  const columns = useChargesShipmentColumns();
  const { setValue } = useFormContext<NoContractForm>();

  const baseTableConfig = createTableConfig<ShipmentChargeDto>({
    onCreatingCancel: () => {},
    onEditingCancel: () => {},
  });

  const table = useMaterialReactTable({
    ...baseTableConfig,
    data,
    enableRowNumbers: false,
    enableSorting: false,
    enablePinning: false,
    enableRowDragging: false,
    enableColumnActions: false,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableEditing,
    editDisplayMode: 'row',
    enableColumnPinning: true,
    positionActionsColumn: 'first',
    initialState: {
      columnPinning: { left: ['mrt-row-actions'] },
    },
    onEditingRowSave: ({ row, values }) => {
      const newData = [...data];
      // Merge edited values with original row to preserve non-editable fields
      newData[row.index] = { ...row.original, ...values };
      setValue('shipmentDeclarationCharges', newData);
      table.setEditingRow(null);
      if (onEditingChange) onEditingChange(false);
    },
    onEditingRowCancel: () => {
      table.setEditingRow(null);
      if (onEditingChange) onEditingChange(false);
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
        size: 120,
      },
    },
    muiTableProps: {
      sx: {
        borderCollapse: 'separate',
      },
    },
    muiTableHeadProps: {
      sx: {
        '& th:first-child': firstCellStyles,
      },
    },
    muiTableBodyProps: {
      sx: {
        '& td:first-child': firstCellStyles,
      },
    },
    muiTableBodyCellProps: {
      sx: {
        borderBottom: '1px solid #DFDFDF !important',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        borderBottom: '1px solid #DFDFDF !important',
      },
    },
    renderRowActions: ({ row }) => (
      <Box sx={{ display: 'flex' }}>
        <Tooltip title='Edit'>
          <IconButton
            onClick={() => {
              table.setEditingRow(row);
              if (onEditingChange) onEditingChange(true);
            }}
          >
            <img src={IconNewEdit} alt='Edit' />
          </IconButton>
        </Tooltip>
        <Tooltip title='Delete'>
          <IconButton
            color='error'
            onClick={() => {
              const newData = data.filter((_, index) => index !== row.index);
              setValue('shipmentDeclarationCharges', newData);
            }}
          >
            <IconTrash />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    columns,
  });

  // Trigger edit mode when editingRowIndex changes
  useEffect(() => {
    if (editingRowIndex !== null && editingRowIndex !== undefined && data[editingRowIndex]) {
      // Get all rows and find the one at the specified index
      const { rows } = table.getRowModel();
      const row = rows[editingRowIndex];
      if (row) {
        table.setEditingRow(row);
        if (onEditingChange) onEditingChange(true);
        if (onEditingRowChange) {
          setTimeout(() => onEditingRowChange(null), 100);
        }
      }
    }
  }, [editingRowIndex, data, table, onEditingRowChange, onEditingChange]);

  return <CustomTable table={table} />;
};
